import { useNavigate } from "react-router-dom";
import type { Channel } from "@/mocks/streams";
import { formatViewers } from "@/lib/format";

interface StreamCardProps {
  channel: Channel;
}

export default function StreamCard({ channel }: StreamCardProps) {
  const navigate = useNavigate();

  return (
    <article
      onClick={() => navigate(`/channel/${channel.id}`)}
      className="group cursor-pointer overflow-hidden rounded-lg bg-background-100 border border-background-200/70 transition-colors hover:border-primary-500/60"
    >
      <div className="relative w-full aspect-video overflow-hidden bg-background-200">
        <img
          src={channel.cover}
          alt={`${channel.title} - ${channel.hostName} - 有因直播`}
          title={`${channel.title} ${channel.hostName}`}
          className="w-full h-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50/80 via-transparent to-transparent" />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          {channel.isLive ? (
            <span className="flex items-center gap-1 rounded-md bg-primary-500 px-2 py-0.5 text-xs font-medium text-background-50">
              <span className="w-1.5 h-1.5 rounded-full bg-background-50" />
              直播中
            </span>
          ) : (
            <span className="rounded-md bg-secondary-800/90 px-2 py-0.5 text-xs font-medium text-foreground-600">离线</span>
          )}
        </div>
        <div className="absolute bottom-3 right-3 flex items-center gap-1 rounded-md bg-background-50/75 px-2 py-0.5 text-xs text-foreground-700">
          <span className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-eye-line text-xs"></i>
          </span>
          {formatViewers(channel.viewers)}
        </div>
      </div>

      <div className="p-4">
        <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-500 transition-colors">
          {channel.title}
        </h3>

        <div className="mt-3 flex items-center gap-2">
          <div className="w-7 h-7 flex-shrink-0 overflow-hidden rounded-full border border-background-300">
            <img src={channel.hostAvatar} alt={`主播 ${channel.hostName}`} className="w-full h-full object-cover" />
          </div>
          <span className="text-xs text-foreground-500">{channel.hostName}</span>
          {channel.hostLevel === "签约" && (
            <span className="rounded-md bg-accent-500/15 px-1.5 py-0.5 text-[10px] font-medium text-accent-400">签约</span>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {channel.tags.map((tag) => (
            <span key={tag} className="rounded-md bg-secondary-100/10 px-2 py-0.5 text-[11px] text-foreground-400">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}